import { useEffect } from 'react'
import { Drawer, Box, CircularProgress, Stack, SvgIcon, Typography } from '@mui/material'
import { Button, ErrorState } from '@ds/components'
import { CARD_COLORS } from '@ds/theme/color-mode'
import { getEnumNameById } from '@modules/api-core'
import type { TPublisherRequest, TEnumItem } from '@modules/types'
import { ShareModal } from '@publisher/modules/share'
import { Chat } from '@publisher/modules/chat'
import { useEnumContext } from '@modules/enums/context/enum'
import { ChatModalProvider, useChatModalContext } from '@publisher/modules/chat/contexts/chat-modal'
import { ShareModalProvider } from '@publisher/modules/share/contexts/shareModal'
import { REQUEST_SHOW_STRINGS } from './strings'
import { useRequestShowData } from './useRequestShowData'

import {
  usePublisherRequestDrawerNav,
  usePublisherRequestDetailsPanel,
  useModalActions,
} from '../../hooks'
import { DrawerState } from '../Drawer'
import { PublisherRequest } from '../PublisherRequest'
import { RequestDetails } from '../RequestWindows'

const panelSx = (open: boolean, flex: string) => ({
  flex: open ? flex : '0 0 0',
  minWidth: 0,
  maxWidth: open ? undefined : 0,
  overflow: open ? 'auto' : 'hidden',
  opacity: open ? 1 : 0,
  borderLeft: open ? 1 : 0,
  borderColor: CARD_COLORS.panelDivider,
  transition: 'opacity 0.2s ease-in-out',
  pointerEvents: open ? 'auto' : 'none',
}) as const

type RequestShowDrawerProps = {
  requestId: number
  selectedRequest: TPublisherRequest
  isFetchingRequest: boolean
  isRequestSubmitted: boolean
  apiUrl: string
  onDrawerClose: () => void
  handleReviewSuccess: () => void
  enums: TEnumItem[]
}

const RequestShowDrawer = ({
  requestId,
  selectedRequest,
  isFetchingRequest,
  isRequestSubmitted,
  apiUrl,
  onDrawerClose,
  handleReviewSuccess,
  enums,
}: RequestShowDrawerProps) => {
  const { detailsOpen, toggleDetails } = usePublisherRequestDetailsPanel(!selectedRequest.withdrawnAt)
  const { handleOpen } = useChatModalContext()

  useEffect(() => {
    if (selectedRequest.withdrawnAt) handleOpen(String(requestId))
  }, [handleOpen, requestId, selectedRequest.withdrawnAt])

  const { chatOpen, handleShare, handleChat } = useModalActions({
    id: requestId,
    selectedRequest,
  })

  const publisherRequest = (
    <PublisherRequest
      requestId={requestId}
      selectedRequest={selectedRequest}
      detailsOpen={detailsOpen}
      chatOpen={chatOpen}
      onShowDetails={toggleDetails}
      onShowChat={handleChat}
      onShare={handleShare}
      onClose={onDrawerClose}
      initialLoading={isFetchingRequest}
      enums={enums}
      apiUrl={apiUrl}
      onDrawerClose={onDrawerClose}
      onReviewSuccess={handleReviewSuccess}
      isRequestSubmitted={isRequestSubmitted}
    />
  )

  return (
    <Drawer
      open
      onClose={onDrawerClose}
      anchor="bottom"
      ModalProps={{
        disableScrollLock: true,
        keepMounted: false,
      }}
      slotProps={{
        paper: {
          sx: {
            top: 30,
            bottom: 30,
            left: 0,
            right: 0,
            mx: 'auto',
            width: 'calc(100% - 80px)',
            maxWidth: 1360,
            maxHeight: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            '& .MuiTypography-root': { lineHeight: 'normal' },
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', bgcolor: 'background.paper' }}>
        <Box
          sx={{
            flex: '999 1 560px',
            minWidth: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          {publisherRequest}
          <ShareModal />
        </Box>
        <Box sx={panelSx(detailsOpen, chatOpen ? '1 1 360px' : '1 1 380px')}>
          <RequestDetails
            request={selectedRequest}
            enums={enums}
            getEnumNameById={getEnumNameById}
          />
        </Box>
        <Box sx={panelSx(chatOpen, detailsOpen ? '1 1 340px' : '1 1 400px')}>
          <Chat closed={Boolean(selectedRequest.withdrawnAt)} />
        </Box>
      </Box>
    </Drawer>
  )
}

const RequestShowContent = () => {
  const { enums } = useEnumContext()
  const { onDrawerClose, handleReviewSuccess } = usePublisherRequestDrawerNav()

  const { id, selectedRequest, isFetchingRequest, isError, isNotFound, isRequestSubmitted, query, apiUrl } =
    useRequestShowData()

  if (query.isLoading) {
    return (
      <DrawerState onClose={onDrawerClose}>
        <CircularProgress />
      </DrawerState>
    )
  }

  if (isNotFound) {
    return (
      <Stack
        component="section"
        alignItems="center"
        justifyContent="center"
        gap={2.5}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 'drawer',
          my: { xs: 1, md: 2, lg: 3 },
          mr: { xs: 1, md: 2, lg: 3 },
          p: 4,
          textAlign: 'center',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <SvgIcon
          aria-hidden="true"
          sx={{
            fontSize: 56,
            color: CARD_COLORS.muted,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.6,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        >
          <path d="M6 3h8l4 4v14H6z" />
          <path d="M10 13l4 4M14 13l-4 4" />
        </SvgIcon>
        <Typography component="h1" sx={{ m: 0, fontSize: 32, fontWeight: 500 }}>
          {REQUEST_SHOW_STRINGS.notFound}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={onDrawerClose}
          sx={{ minHeight: 48, px: 3.5, fontSize: 16, fontWeight: 600 }}
        >
          {REQUEST_SHOW_STRINGS.backToRequests}
        </Button>
      </Stack>
    )
  }

  if (isError || !selectedRequest || id === undefined) {
    return (
      <DrawerState onClose={onDrawerClose}>
        <ErrorState
          message={REQUEST_SHOW_STRINGS.failedToLoadRequest}
          severity="error"
          onRetry={() => query.refetch()}
          retryText={REQUEST_SHOW_STRINGS.retry}
        />
      </DrawerState>
    )
  }

  return (
    <RequestShowDrawer
      key={id}
      requestId={id}
      selectedRequest={selectedRequest}
      isFetchingRequest={isFetchingRequest}
      isRequestSubmitted={isRequestSubmitted}
      apiUrl={apiUrl}
      onDrawerClose={onDrawerClose}
      handleReviewSuccess={handleReviewSuccess}
      enums={enums}
    />
  )
}

export const RequestShow = () => {
  return (
    <ShareModalProvider>
      <ChatModalProvider>
        <RequestShowContent />
      </ChatModalProvider>
    </ShareModalProvider>
  )
}
